import clsx from 'clsx';
import HelpError from 'components/Form/HelpError';
import {useTooltip} from 'components/Tooltip/hook';
import {forwardRef, InputHTMLAttributes, ReactNode} from 'react';
import {FieldError} from 'react-hook-form';

export type BaseCheckboxProps<T = HTMLInputElement> = {
    className?: string;
    error?: FieldError;
    helpText?: ReactNode;
    label?: ReactNode;
    name: string;
    type?: never;
} & InputHTMLAttributes<T>;

const BaseCheckbox = forwardRef<HTMLInputElement, BaseCheckboxProps>(
    (
        {
            className,
            disabled,
            error,
            helpText,
            id,
            label,
            name,
            readOnly,
            required,
            ...rest
        },
        ref,
    ) => {
        const {setTooltipRef} = useTooltip();

        const checkbox = (
            <input
                className={clsx(!label && className)}
                id={id || name}
                name={name}
                ref={ref}
                required={required && !!error}
                type="checkbox"
                {...rest}
                disabled={disabled || readOnly}
            />
        );

        const helpError =
            helpText || error ? (
                <HelpError
                    disabled={disabled}
                    error={error}
                    helpText={helpText}
                    label={label}
                />
            ) : null;

        if (!label)
            return (
                <>
                    {checkbox}
                    {helpError}
                </>
            );

        const field = (
            <label
                className={clsx(
                    'group inline-flex w-fit select-none items-center',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    !helpError && className,
                )}
                htmlFor={id || name}
                ref={(r) => {
                    setTooltipRef(r);
                }}
            >
                {checkbox}
                <div
                    className={clsx(
                        'ml-2',
                        disabled || readOnly
                            ? 'text-light-disabled'
                            : 'text-grey-900',
                    )}
                >
                    {label}
                </div>
            </label>
        );

        if (!helpError) return field;

        return (
            <div className={className}>
                {field}
                {helpError}
            </div>
        );
    },
);
BaseCheckbox.displayName = 'BaseCheckbox';

export default BaseCheckbox;
